import { FolderOutlined, IconStandardBox } from '../../../components/icons'
import { useToast } from '../../../components/ui/ToastProvider'
import { formatDateIso, formatPrice } from '../format'
import type { BomLibraryStatus, LibraryBomRow } from '../types'
import { BomStatusChip } from './BomStatusChip'

export interface BomCardGridProps {
  readonly items: readonly LibraryBomRow[]
  readonly onOpen: (id: string) => void
  /** UID-295 AC4 (QA fix round) — wires the card's status chip dropdown to
   * `useBoms().updateStatus`. Optional so callers/tests that don't need
   * inline status change still render the read-only chip (unchanged
   * behavior). */
  readonly onStatusChange?: (bomId: string, status: BomLibraryStatus) => Promise<void>
}

/**
 * UID-295/296 measured design spec §A3: the "Approved" card's folder tile is
 * violet (`rgb(243,238,255)`, an exact match of `--color-accent-violet-bg`);
 * "In review"/"Draft" cards are blue (`rgb(239,241,251)`, an exact match of
 * `--color-brand-tint-50`). The spec's 3-card sample didn't include an
 * `ordered` example — defaulted to blue here (same family as
 * draft/in_review) rather than guessing a third tint; flag to design if
 * `ordered` should read violet too.
 */
const BOM_CARD_ICON_TINT: Record<BomLibraryStatus, 'blue' | 'violet'> = {
  draft: 'blue',
  in_review: 'blue',
  approved: 'violet',
  ordered: 'blue',
}

const MAX_THUMBS = 4

/**
 * UID-295 — the BOMs tab's card grid (screens.md §5, measured spec §A2/A3),
 * replacing the plain `BomsTable`. A responsive auto-fill grid (4/2/1 cols
 * across breakpoints, see `.bom-card-grid` in parts.css) of clickable cards:
 * 44×44 tinted folder tile, title + description, metadata row (parts count,
 * cost, updated date), a status chip, and a thumbnail strip.
 *
 * UID-295 code-review fix (B2, QA fix round) — the card is NOT one giant
 * clickable region anymore. A previous version made the whole card a
 * `role="button"` div that WRAPPED the status chip's own interactive
 * `<button>` — a nested-interactive-content anti-pattern (WCAG 4.1.2: a
 * control's accessibility tree can't contain another focusable control;
 * `stopPropagation` only stops the click from ALSO firing the outer
 * handler, it doesn't fix the structural nesting axe-core's
 * `nested-interactive` rule flags). The fix: there is now exactly ONE
 * primary control per card — a real `<button type="button">`
 * (`.bom-card-primary`) wrapping the icon tile/title/description/meta row,
 * with `aria-label={bom.name}` so its accessible name is just the BOM name
 * (not the whole card's concatenated text) — and the status chip lives OUTSIDE
 * that button, as a sibling in the footer. CSS (`.bom-card`/`.bom-card-primary`
 * in parts.css) preserves the original visual layout; only the a11y
 * structure changed. Verified with `expectNoA11yViolations`
 * (axe-core) in `BomCardGrid.test.tsx` — no violations.
 */
export function BomCardGrid({ items, onOpen, onStatusChange }: BomCardGridProps) {
  return (
    <div className="bom-card-grid">
      {items.map((bom) => (
        <BomCard
          key={bom.id}
          bom={bom}
          onOpen={onOpen}
          {...(onStatusChange ? { onStatusChange } : {})}
        />
      ))}
    </div>
  )
}

function BomCard({
  bom,
  onOpen,
  onStatusChange,
}: {
  readonly bom: LibraryBomRow
  readonly onOpen: (id: string) => void
  readonly onStatusChange?: (bomId: string, status: BomLibraryStatus) => Promise<void>
}) {
  const tint = BOM_CARD_ICON_TINT[bom.status]
  const thumbnails = (bom.thumbnailUrls ?? []).slice(0, MAX_THUMBS)
  // AC3 (QA fix round) — "+N" overflow indicator: how many of this BOM's
  // parts aren't visually represented by the (up-to-4) thumbnails shown,
  // whether because the BOM has more parts than the cap or because some
  // parts simply have no thumbnail yet.
  const overflowCount = Math.max(0, bom.partCount - thumbnails.length)
  const { showToast } = useToast()

  async function handleStatusChange(next: BomLibraryStatus) {
    if (!onStatusChange) return
    try {
      await onStatusChange(bom.id, next)
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : 'We couldn’t update this BOM’s status. Try again.',
        'error',
      )
    }
  }

  return (
    <div className="bom-card">
      {/* B2 (QA fix round) — the ONE primary control for the card. A real
          <button> (not role="button" on a div) so Enter/Space activation,
          focus styling, and the accessible name are all native — no custom
          keydown handling needed. `aria-label` keeps its accessible name to
          just the BOM name, since (unlike the old whole-card control) it no
          longer needs to speak the footer's status text too. */}
      <button
        type="button"
        className="bom-card-primary"
        aria-label={bom.name}
        onClick={() => onOpen(bom.id)}
      >
        <div className="bom-card-header">
          <span className={`bom-card-icon-tile bom-card-icon-tile--${tint}`} aria-hidden="true">
            <FolderOutlined />
          </span>
          <span className="bom-card-heading">
            <span className="bom-card-title">{bom.name}</span>
            {bom.description && <span className="bom-card-description">{bom.description}</span>}
          </span>
        </div>

        <div className="bom-card-meta-row">
          <span className="bom-card-meta-item">
            <IconStandardBox size={15} />
            {bom.partCount} part{bom.partCount === 1 ? '' : 's'}
          </span>
          {/* Measured spec §A3: the "$" is a literal text char (not an icon).
              `formatPrice` already prepends the currency symbol, so this
              renders it as ONE bold amount rather than a duplicated "$$". */}
          <span className="bom-card-meta-item bom-card-meta-cost-amount">
            {bom.estimatedCost ? formatPrice(bom.estimatedCost) : '—'}
          </span>
          {/* UID-378 — the measured design spec's card date is ISO
              (`YYYY-MM-DD`) and right-aligned within the meta row, unlike
              every other localized date in the app (`formatDate`'s "Aug 28,
              2026", untouched everywhere else). `.bom-card-meta-date`
              carries the right-alignment (margin-left: auto). */}
          <span className="bom-card-meta-item bom-card-meta-date">
            {formatDateIso(bom.updatedAt)}
          </span>
        </div>
      </button>

      <div className="bom-card-footer">
        {/* B2 (QA fix round) — the status chip is now a SIBLING of the
            primary control, not nested inside it, so its own dropdown
            <button> is no longer a nested-interactive violation. No
            stopPropagation needed either — there's no ancestor click
            handler left to bubble into.
            UID-378 — the card's status pill is a soft-filled pill with no
            leading icon (measured design spec), unlike the BOM detail
            header's chip which keeps both — `hideIcon` + the
            `bom-card-status-chip` class (parts.css) are scoped to just this
            one call site. */}
        <BomStatusChip
          status={bom.status}
          hideIcon
          className="bom-card-status-chip"
          {...(onStatusChange ? { onChange: handleStatusChange } : {})}
        />
        {/* UID-378 — the strip now renders on EVERY card (previously hidden
            entirely for a 0-thumbnail BOM), matching the design's "always
            show ~4 bordered thumbnail boxes" treatment. A BOM with no
            thumbnails yet falls back to the SAME bordered placeholder-box
            convention `BomOrderHistoryCard`'s line thumbnails use
            (`.bom-order-history-card-line-thumb-placeholder`) rather than
            leaving the strip visually empty. */}
        <div className="bom-card-thumb-strip" aria-hidden="true">
          {thumbnails.length > 0 ? (
            <>
              {thumbnails.map((url, index) => (
                <span
                  // Decorative, aria-hidden thumbnail swatches — url isn't
                  // guaranteed unique across a BOM's parts (two lines can
                  // share one thumbnail).
                  // biome-ignore lint/suspicious/noArrayIndexKey: see above
                  key={index}
                  className="bom-card-thumb"
                  style={{ backgroundImage: `url(${url})` }}
                />
              ))}
              {overflowCount > 0 && (
                <span className="bom-card-thumb-overflow">+{overflowCount}</span>
              )}
            </>
          ) : (
            <span className="bom-card-thumb-placeholder" />
          )}
        </div>
      </div>
    </div>
  )
}
